import { Slot } from "@radix-ui/react-slot";
import { useMemo } from "react";
import type { Ref } from "react";
import type { PathPattern } from "react-router";
import {
	Link as _Link,
	createPath,
	matchPath,
	resolvePath,
	useLocation
} from "react-router";

import { replaceLanguage, useLocale } from "~/i18n";
import type { Locale } from "~/i18n";
import { preloadRoute } from "~/preload";
import { isInternalHref } from "~/urls";

export type LinkProps = {
	active?: boolean;
	asChild?: boolean;
	as?: string;
	internal?: boolean;
} & {
	href: Parameters<typeof _Link>[0]["to"] | null;
	pattern?: PathPattern<string>;
	hrefLang?: Locale;
} & Omit<Parameters<typeof _Link>[0], "hrefLang" | "to">;

export function Link({
	ref: reference,
	href: _href,
	pattern: _pattern,
	active: _active,
	asChild = false,
	hrefLang: _hrefLang,
	as = "span",
	target,
	internal: _internal,
	...props
}: { ref?: Ref<HTMLAnchorElement> | null } & LinkProps) {
	const location = useLocation();

	const [locale] = useLocale();
	const hrefLang = _hrefLang || locale;

	const { to, internal, active } = useMemo(() => {
		const internal = _internal === undefined
			? (_href ? isInternalHref(_href) : true)
			: _internal;

		const to = internal && _href
			? createPath(replaceLanguage(_href, hrefLang, location.pathname))
			: _href || "#";

		const pattern = _pattern || (internal && to ? { path: resolvePath(to, location.pathname).pathname } : undefined);
		const active = _active || (pattern && matchPath(pattern, location.pathname) !== null) || false;

		return { to, internal, active };
	}, [_active, _href, _pattern, hrefLang, _internal, location.pathname]);

	const Component = asChild
		? Slot
		: _href === null
			? as
			: _Link;

	return (
		<Component
			data-active={active ? "" : undefined}
			data-external={internal ? undefined : ""}
			hrefLang={hrefLang}
			prefetch="intent"
			onPointerEnter={() => preloadRoute(to)}
			{...props}
			ref={reference}
			target={target || (internal ? undefined : "_blank")}
			to={to}
		/>
	);
}
